/**
 * @file Swinging a melee weapon (issues #96, #122).
 *
 * This is the trigger for any weapon whose row says `type: 'melee'`, the same
 * arrangement `StreamFlameSystem` has with streaming weapons: `WeaponSystem`
 * steps aside, and the swing is resolved here as an arc rather than as rounds.
 *
 * It used to fire only for a player with *no* `Weapon` at all, which is why the
 * knife had no model in hand, no HUD card and no tunable stats. The knife is now
 * a real equipped weapon (`FallbackWeaponSystem` hands it out), so this reads its
 * numbers off the component, so an override in the weapon tuning layer reaches
 * the next knife equipped. The debug window's Weapons tab does not list it —
 * `isInspectableWeapon` hides melee rows on purpose (issue #123) — so the knife
 * is tuned through the layer or the table, not through that panel.
 *
 * Presentation is deliberately not a gunshot: no muzzle flash, no round, no
 * straight trail. One arc of particles sweeps the swing and the held model turns
 * with it, driven by `swingTimer` on the weapon.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Transform, PlayerControlled, Health, Collider, AIBrain, RangedAI } from '../components/index.js';
import { Weapon } from '../components/Weapon.js';
import { Action } from '../input/InputManager.js';
import { Events } from '../core/EventBus.js';
import { PALETTE } from '../config/Palette.js';
import { raycastAABB, faceNormal } from '../physics/AABB.js';

export class MeleeWeaponSystem extends System {
  /** Runs after WeaponSystem (25) so ranged weapons take priority. */
  static priority = 26;

  constructor() {
    super();
    /** @type {THREE.Vector3} @private */
    this._dir = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitPoint = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitNormal = new THREE.Vector3();
    /** @type {{ x: number, z: number }} @private */
    this._normal = { x: 0, z: 0 };
    /** @type {object} @private */
    this._flashEvent = {
      position: this._hitPoint,
      color: PALETTE.muzzle,
      intensity: 5,
      life: 0.04
    };
    /** @type {object} @private */
    this._shakeEvent = { amount: 0 };
    /** @type {object} @private */
    this._impactEvent = { position: this._hitPoint, normal: this._hitNormal, target: null };
    /** @type {object} @private */
    this._damageEvent = { target: null, amount: 0, source: null, point: this._hitPoint };
    /** @type {object} @private */
    this._weaponHitEvent = { weapon: null, target: null, position: this._hitPoint, damage: 0 };
    /** @type {object} @private */
    this._missEvent = { weapon: null, owner: null };
    /**
     * Reused swing payload. `position` is re-pointed at the swinger's transform
     * rather than copied: the arc is drawn the instant this is emitted.
     * @type {object} @private
     */
    this._swingEvent = {
      position: null,
      direction: new THREE.Vector3(),
      range: 0,
      arcDegrees: 0,
      duration: 0,
      height: 0,
      color: PALETTE.impact
    };
    /** @type {number} Last melee fire time, per player. @private */
    this._lastFire = new Map();
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    for (const player of this.world.query(PlayerControlled, Transform, Health, Weapon)) {
      const knife = player.get(Weapon);
      // Only melee weapons: everything else is `WeaponSystem`'s business.
      if (!knife.melee) continue;

      // The swing is what the held model is posed from, so it ticks down here
      // whether or not the trigger is pressed again.
      if (knife.swingTimer > 0) knife.swingTimer = Math.max(0, knife.swingTimer - dt);

      const cooldown = 1 / knife.fireRate;
      const transform = player.get(Transform);
      const control = player.get(PlayerControlled);

      // Resolve aim direction.
      const aimPoint = control?.aimPoint;
      let dx, dz;
      if (aimPoint) {
        dx = aimPoint.x - transform.position.x;
        dz = aimPoint.z - transform.position.z;
      } else {
        dx = Math.sin(transform.rotationY);
        dz = Math.cos(transform.rotationY);
      }
      const len = Math.sqrt(dx * dx + dz * dz);
      if (len < 1e-6) continue;
      dx /= len;
      dz /= len;

      // Held, not edge-triggered: the cooldown below is what paces swings, so
      // an edge check only broke auto-fire (a touch aim stick holds FIRE down,
      // which would have bought exactly one swing per stick flick).
      if (!this.ctx.input.isDown(Action.FIRE)) continue;

      // Respect the fire cooldown.
      const lastFireTime = this._lastFire.get(player) ?? 0;
      if (this.ctx.time.elapsed - lastFireTime < cooldown) continue;

      this._meleeSwing(player, knife, dx, dz, transform);
      this._lastFire.set(player, this.ctx.time.elapsed);
    }
  }

  /**
   * Execute a melee swing: hitscan along the aim direction with arc check.
   *
   * @param {import('../ecs/Entity.js').Entity} player The unarmed player.
   * @param {import('../config/Weapons.js').WeaponArchetype} knife Knife archetype.
   * @param {number} dirX Normalised aim direction X.
   * @param {number} dirZ Normalised aim direction Z.
   * @param {import('../components/Transform.js').Transform} transform Player transform.
   * @private
   */
  _meleeSwing(player, knife, dirX, dirZ, transform) {
    // Presentation first: the arc has to appear whether or not the blade
    // connects, or a miss reads as an input that never registered.
    knife.swingTimer = knife.swingSeconds;
    this._swingEvent.position = transform.position;
    this._swingEvent.direction.set(dirX, 0, dirZ);
    this._swingEvent.range = knife.range;
    this._swingEvent.arcDegrees = knife.meleeArcDegrees;
    this._swingEvent.duration = knife.swingSeconds;
    this._swingEvent.height = knife.muzzleHeight;
    this.ctx.events.emit(Events.MELEE_SWING, this._swingEvent);

    const originX = transform.position.x + dirX * knife.muzzleForward;
    const originZ = transform.position.z + dirZ * knife.muzzleForward;
    const endX = originX + dirX * knife.range;
    const endZ = originZ + dirZ * knife.range;
    const muzzleY = knife.muzzleHeight;

    // Collect hits within the melee arc.
    const arcRad = (knife.meleeArcDegrees ?? 120) * Math.PI / 180;
    const hits = [];

    for (const other of this.world.query(Transform, Collider, Health)) {
      if (other === player || !other.alive) continue;
      if (!other.has(AIBrain) && !other.has(RangedAI)) continue; // only damage enemies

      const collider = other.get(Collider);
      if (collider.isTrigger) continue;

      const pos = other.get(Transform).position;
      const cx = pos.x;
      const cz = pos.z;

      // Check if enemy is within the melee arc from the player's facing.
      const toEnemyX = cx - transform.position.x;
      const toEnemyZ = cz - transform.position.z;
      const toEnemyLen = Math.sqrt(toEnemyX * toEnemyX + toEnemyZ * toEnemyZ);
      if (toEnemyLen > knife.range + collider.halfX) continue;

      // Check if the enemy is within the arc angle.
      const enemyAngle = Math.atan2(toEnemyX, toEnemyZ);
      const aimAngle = Math.atan2(dirX, dirZ);
      let angleDiff = enemyAngle - aimAngle;
      // Normalize to [-PI, PI]
      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
      if (Math.abs(angleDiff) > arcRad / 2) continue;

      // Check if the enemy is within the trace line (simple distance check).
      const dist = Math.sqrt(
        (cx - originX) * (cx - originX) + (cz - originZ) * (cz - originZ)
      );
      if (dist > knife.range) continue;

      // Check AABB intersection with the trace.
      const t = raycastAABB(
        originX, originZ,
        endX - originX, endZ - originZ,
        cx - collider.halfX, cz - collider.halfZ,
        cx + collider.halfX, cz + collider.halfZ
      );
      if (t < 0 || t > 1) continue;

      // Distance-sorted near-to-far.
      hits.push({ t, entity: other });
    }

    if (hits.length === 0) {
      this._missEvent.weapon = knife;
      this._missEvent.owner = player;
      this.ctx.events.emit(Events.WEAPON_MISSED, this._missEvent);
      return;
    }

    // Sort near to far.
    hits.sort((a, b) => a.t - b.t);

    for (const hit of hits) {
      const target = hit.entity;

      this._hitPoint.set(
        originX + (endX - originX) * hit.t,
        muzzleY,
        originZ + (endZ - originZ) * hit.t
      );
      this._faceNormalOf(target);

      // Deal damage.
      this._damageEvent.target = target;
      this._damageEvent.amount = knife.damage;
      this._damageEvent.source = player;
      this.ctx.events.emit(Events.DAMAGE, this._damageEvent);

      this._impactEvent.target = target;
      this.ctx.events.emit(Events.BULLET_HIT, this._impactEvent);

      this._weaponHitEvent.weapon = knife;
      this._weaponHitEvent.target = target;
      this._weaponHitEvent.damage = knife.damage;
      this.ctx.events.emit(Events.WEAPON_HIT, this._weaponHitEvent);

      // Melee only hits the first target (no pierce).
      return;
    }
  }

  _faceNormalOf(target) {
    const collider = target.get(Collider);
    const position = target.get(Transform).position;
    faceNormal(
      this._hitPoint.x, this._hitPoint.z,
      position.x, position.z,
      collider.halfX + 1e-4, collider.halfZ + 1e-4,
      this._normal
    );
    this._hitNormal.set(this._normal.x, 0, this._normal.z);
  }

  /** @returns {void} */
  dispose() {
    this._lastFire.clear();
  }
}